import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const CompanyProfileSection = ({ profile, onUpdate, isEditing, onToggleEdit }) => {
  const [formData, setFormData] = useState(profile);
  const [logoFile, setLogoFile] = useState(null);

  const companyTypes = [
    { value: 'individual_agent', label: 'Individual Agent' },
    { value: 'insurance_agency', label: 'Insurance Agency' },
    { value: 'brokerage', label: 'Insurance Brokerage' },
    { value: 'call_center', label: 'Call Center' },
    { value: 'enterprise', label: 'Enterprise Insurance Company' }
  ];

  const licenseStates = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        handleInputChange('companyLogo', e?.target?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    onUpdate(formData);
    onToggleEdit();
  };

  const handleCancel = () => {
    setFormData(profile);
    setLogoFile(null);
    onToggleEdit();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Building2" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Company Profile</h3>
            <p className="text-sm text-muted-foreground">Basic company information and licensing details</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onToggleEdit} iconName="Edit2">
              Edit Profile
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Logo */}
        <div className="lg:col-span-1">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-4 bg-muted rounded-lg overflow-hidden border-2 border-dashed border-border">
              {formData?.companyLogo ? (
                <Image
                  src={formData?.companyLogo}
                  alt="Company Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Building2" size={32} className="text-muted-foreground" />
                </div>
              )}
            </div>
            {isEditing && (
              <div>
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                  iconName="Upload"
                >
                  Upload Logo
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Company Information */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Company Name"
              type="text"
              value={formData?.companyName}
              onChange={(e) => handleInputChange('companyName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Select
              label="Company Type"
              options={companyTypes}
              value={formData?.companyType}
              onChange={(value) => handleInputChange('companyType', value)}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="License Number"
              type="text"
              value={formData?.licenseNumber}
              onChange={(e) => handleInputChange('licenseNumber', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Select
              label="Licensed States"
              options={licenseStates}
              value={formData?.licensedStates}
              onChange={(value) => handleInputChange('licensedStates', value)}
              disabled={!isEditing}
              multiple
              searchable
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Primary Contact Name"
              type="text"
              value={formData?.primaryContactName}
              onChange={(e) => handleInputChange('primaryContactName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label="Primary Contact Email"
              type="email"
              value={formData?.primaryContactEmail}
              onChange={(e) => handleInputChange('primaryContactEmail', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Phone Number"
              type="tel"
              value={formData?.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label="Website"
              type="url"
              value={formData?.website}
              onChange={(e) => handleInputChange('website', e?.target?.value)}
              disabled={!isEditing}
            />
          </div>

          <div>
            <Input
              label="Business Address"
              type="text"
              value={formData?.businessAddress}
              onChange={(e) => handleInputChange('businessAddress', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="City"
              type="text"
              value={formData?.city}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label="State"
              type="text"
              value={formData?.state}
              onChange={(e) => handleInputChange('state', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label="ZIP Code"
              type="text"
              value={formData?.zipCode}
              onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>
        </div>
      </div>
      {/* Verification Status */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={20} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Verification Status</p>
              <p className="text-xs text-muted-foreground">Profile verified on {new Date()?.toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">Verified</span>
            <Button variant="ghost" size="sm" iconName="ExternalLink">
              View Certificate
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileSection;